import { FC, useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Button,
  Spinner,
  Separator,
} from '@chakra-ui/react';
import { LuArrowLeft, LuCheck, LuX, LuTriangleAlert } from 'react-icons/lu';
import { AIAnalysisHistory } from '@/types/aiManagement';
import { aiAnalysisHistoryService } from '@/services/firebase/aiAnalysisHistoryService';
import { useAuthStore } from '@/stores/authStore';
import { AppLayout } from '@/components/layout/AppLayout';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

/**
 * AI分析履歴詳細ページ
 */
export const AIAnalysisHistoryDetailPage: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const fetchedRef = useRef(false);

  const [history, setHistory] = useState<AIAnalysisHistory | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id && user && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchHistory();
    }
  }, [id, user]);

  const fetchHistory = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await aiAnalysisHistoryService.getHistoryById(id);
      if (!data) {
        alert('履歴が見つかりません');
        navigate('/admin/analysis-history');
        return;
      }
      setHistory(data);
    } catch (error) {
      console.error('履歴取得エラー:', error);
      alert('履歴の取得に失敗しました');
      navigate('/admin/analysis-history');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <VStack gap={4} py={8}>
          <Spinner size="xl" />
          <Text>読み込み中...</Text>
        </VStack>
      </AppLayout>
    );
  }

  if (!history) {
    return (
      <AppLayout>
        <VStack gap={4} py={8}>
          <Text>履歴が見つかりません</Text>
          <Button onClick={() => navigate('/admin/analysis-history')}>
            一覧に戻る
          </Button>
        </VStack>
      </AppLayout>
    );
  }

  const suggestions = history.output.suggestions || [];

  return (
    <AppLayout>
      <VStack align="stretch" gap={6}>
        {/* ヘッダー */}
        <HStack justify="space-between">
          <HStack gap={4}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/analysis-history')}
            >
              <LuArrowLeft />
            </Button>
            <Text fontSize="2xl" fontWeight="bold">
              分析詳細
            </Text>
          </HStack>
        </HStack>

        {/* メタ情報 */}
        <Box bg="white" p={6} borderRadius="lg" shadow="sm">
          <HStack justify="space-between" flexWrap="wrap" gap={4}>
            <HStack gap={4}>
              <Box>
                <Text fontSize="sm" color="gray.600">
                  実行日時
                </Text>
                <Text fontWeight="medium">
                  {format(history.createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss', {
                    locale: ja,
                  })}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600">
                  ステータス
                </Text>
                {history.status === 'success' ? (
                  <Badge colorPalette="green">
                    <LuCheck />
                    成功
                  </Badge>
                ) : (
                  <Badge colorPalette="red">
                    <LuX />
                    エラー
                  </Badge>
                )}
              </Box>
            </HStack>
            <HStack gap={4}>
              <Box>
                <Text fontSize="sm" color="gray.600">
                  実行時間
                </Text>
                <Text fontWeight="medium">
                  {(history.executionTime / 1000).toFixed(1)}秒
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.600">
                  使用プロンプト
                </Text>
                <Text fontWeight="medium">
                  プロンプト (v{history.promptVersion})
                </Text>
              </Box>
            </HStack>
          </HStack>
        </Box>

        {/* 入力データ */}
        <Box bg="white" p={6} borderRadius="lg" shadow="sm">
          <Text fontWeight="bold" fontSize="lg" mb={4}>
            入力データ
          </Text>
          <Box bg="gray.50" p={4} borderRadius="md">
            <HStack mb={2}>
              <Text fontSize="sm" color="gray.600">
                対象メス犬:
              </Text>
              <Text fontWeight="medium">{history.input.femaleName}</Text>
            </HStack>
            <Box>
              <Text fontSize="sm" color="gray.600" mb={1}>
                候補オス犬:
              </Text>
              <Text fontSize="sm">{history.input.candidateNames.join(', ')}</Text>
            </Box>
          </Box>
        </Box>

        <Separator />

        {/* 結果 */}
        <Box bg="white" p={6} borderRadius="lg" shadow="sm">
          <Text fontWeight="bold" fontSize="lg" mb={4}>
            AI提案結果
          </Text>

          {history.status === 'error' ? (
            <Box bg="red.50" p={4} borderRadius="md">
              <HStack color="red.600">
                <LuX />
                <Text fontWeight="medium">エラーが発生しました</Text>
              </HStack>
              {history.errorMessage && (
                <Text fontSize="sm" color="red.600" mt={2}>
                  {history.errorMessage}
                </Text>
              )}
            </Box>
          ) : suggestions.length === 0 ? (
            <Box bg="yellow.50" p={4} borderRadius="md">
              <Text color="yellow.700">提案結果がありません</Text>
            </Box>
          ) : (
            <VStack align="stretch" gap={4}>
              {suggestions.map((suggestion, index) => (
                <Box
                  key={suggestion.maleId}
                  bg="white"
                  p={5}
                  borderWidth={2}
                  borderRadius="lg"
                  borderColor={index === 0 ? 'blue.300' : 'gray.200'}
                >
                  <HStack justify="space-between" mb={3}>
                    <HStack gap={3}>
                      <Badge
                        colorPalette={
                          index === 0 ? 'yellow' : index === 1 ? 'gray' : 'orange'
                        }
                        fontSize="md"
                        px={3}
                        py={1}
                      >
                        {index + 1}位
                      </Badge>
                      <Text fontWeight="bold" fontSize="lg">
                        {suggestion.maleName}
                      </Text>
                    </HStack>
                    <Badge colorPalette="blue" fontSize="lg" px={3} py={1}>
                      スコア: {suggestion.score}
                    </Badge>
                  </HStack>

                  {/* 推奨理由 */}
                  {suggestion.reasons && suggestion.reasons.length > 0 && (
                    <Box mt={3}>
                      <Text fontSize="sm" color="gray.600" mb={2}>
                        推奨理由:
                      </Text>
                      <VStack align="stretch" gap={1}>
                        {suggestion.reasons.map((reason, i) => (
                          <HStack key={i} fontSize="sm">
                            <LuCheck color="green" />
                            <Text>{reason}</Text>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  )}

                  {/* 警告 */}
                  {suggestion.warnings && suggestion.warnings.length > 0 && (
                    <Box mt={3}>
                      <Text fontSize="sm" color="orange.600" mb={2}>
                        警告:
                      </Text>
                      <VStack align="stretch" gap={1}>
                        {suggestion.warnings.map((warning, i) => (
                          <HStack key={i} fontSize="sm" color="orange.600">
                            <LuTriangleAlert />
                            <Text>{warning}</Text>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  )}
                </Box>
              ))}
            </VStack>
          )}
        </Box>

        {/* 戻るボタン */}
        <Box>
          <Button
            variant="outline"
            onClick={() => navigate('/admin/analysis-history')}
          >
            <LuArrowLeft />
            一覧に戻る
          </Button>
        </Box>
      </VStack>
    </AppLayout>
  );
};

export default AIAnalysisHistoryDetailPage;
